/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { Link } from '@fluentui/react';
import { ChoiceGroup, IChoiceGroupOption } from '@fluentui/react/lib/ChoiceGroup';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { IStackItemStyles, IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logDTButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { AzureBlobContainerStore } from 'src/core/stores/AzureBlobContainerStore';
import { AzureBlobStore } from 'src/core/stores/AzureBlobStore';
import { AzureStorageAccountStore } from 'src/core/stores/AzureStorageAccountStore';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { TargetFile, useTranslatorCapabilities } from 'src/languageStudio/core/hooks/useTranslatorCapabilities';
import { TranslationBlobStoreTable } from 'src/translation/components/TranslationBlobStoreTable';
import {
    TranslationCreationSteps,
    TranslationFileSourceOptions,
    TranslationInfoDto
} from 'src/translation/components/TranslationCreationPage';
import { TranslationFileLocationDropDowns } from 'src/translation/components/TranslationFileLocationDropDowns';
import { TranslationLocalFileTable } from 'src/translation/components/TranslationLocalFileTable';
import { TranslationQuotaConsumptionCard } from 'src/translation/components/TranslationQuotaConsumptionCard';
import { TranslationToolFooter } from 'src/translation/components/TranslationToolFooter';
import { TranslationUploadFileLocationModal } from 'src/translation/components/TranslationUploadFilesLocationModal';

export type TranslationFilesSelectionPageProps = {
    onSubmit: () => void;
    onDismiss: () => void;
    onPrevious: () => void;
    onNext: (
        filesLocation: TranslationFileSourceOptions,
        uploadContainerName: string,
        files: TargetFile[],
        containerIsAllSelected: boolean,
        uploadedLocalFiles: File[],
        selectedBlobFiles: AzureBlobStore[]
    ) => void;
    uploadedDocs: string[];
    isReturningUser: boolean;
    blobFiles: AzureBlobStore[];
    isBlobFilesLoading: boolean;
    isStorageAccountsLoading: boolean;
    createdStorageContainerName: string;
    isStorageContainersLoading: boolean;
    activeStep: TranslationCreationSteps;
    translationConfigsDto: TranslationInfoDto;
    storageAccounts: AzureStorageAccountStore[];
    storageContainers: AzureBlobContainerStore[];
    selectedStorageAccountStore: AzureStorageAccountStore;
    fetchStorageAccountContainers: () => void;
    onUploadedDocsChange: (uploadedFiles: string[]) => void;
    onChangeStorageContainer: (containerName: string) => void;
    onChangeSelectedLocalFiles: (selectedLocalFiles: File[]) => void;
    onChangeSelectedBlobFiles: (selectedBlobFiles: AzureBlobStore[]) => void;
};

const messages = defineMessages({
    changeButton: { id: 'translation.translationFilesSelectionPage.changeButton', defaultMessage: 'Change' },
    pageTitle: { id: 'translation.translationFilesSelectionPage.pageTitle', defaultMessage: 'Select files for translation' },
    pageSubtitle: {
        id: 'translation.translationFilesSelectionPage.pageSubtitle',
        defaultMessage: 'Select where files to translate are located. You can either select files from a Blob store or upload local files.'
    },
    fileOptionsHeader: { id: 'translation.translationFilesSelectionPage.fileOptionsHeader', defaultMessage: 'Select file location' },
    localFilesOption: { id: 'translation.translationFilesSelectionPage.localFilesOption', defaultMessage: 'Upload local files' },
    blobStoreOption: { id: 'translation.translationFilesSelectionPage.blobStoreOption', defaultMessage: 'Select from Blob storage' },
    fileLocationSubtitle: {
        id: 'translation.translationFilesSelectionPage.fileLocationSubtitle',
        defaultMessage: 'Select the files you wish to translate. You can select individual files or entire folders.'
    },
    fileDisclaimerSubtitle: {
        id: 'translation.translationFilesSelectionPage.fileDisclaimerSubtitle',
        defaultMessage: 'Please upload {sourceLanguage} documents only.'
    },
    storageAccountTitle: {
        id: 'translation.translationFilesSelectionPage.storageAccountTitle',
        defaultMessage: 'Storage account'
    },
    blobContainerTitle: {
        id: 'translation.translationFilesSelectionPage.blobContainerTitle',
        defaultMessage: 'Blob container'
    },
    selectBlobContainerPlaceholder: {
        id: 'translation.translationFilesSelectionPage.selectBlobContainerPlaceholder',
        defaultMessage: 'Select a Blob container to view files'
    },
    storageAccountDescription: {
        id: 'translation.translationFilesSelectionPage.storageAccountDescription',
        defaultMessage: 'This is the storage account you have selected during initial configuration'
    },
    blobContainerDescription: {
        id: 'translation.translationFilesSelectionPage.blobContainerDescription',
        defaultMessage:
            'This is the container within your Azure storage account {storageAccount} that contains the documents you want to translate'
    },
    downloadedFileSuccess: {
        id: 'translation.translationFilesSelectionPage.downloadedFileSuccess',
        defaultMessage: 'Downloaded file succeeded'
    },
    downloadedFileFailed: {
        id: 'translation.translationFilesSelectionPage.downloadedFileFailed',
        defaultMessage: 'Downloaded file failed'
    },
    downloadedFileFailedDescription: {
        id: 'translation.translationFilesSelectionPage.downloadedFileFailedDescription',
        defaultMessage: 'The file might be deleted or corrupted'
    },
    selectFileLocationDescription: {
        id: 'translation.translationFilesSelectionPage.selectFileLocationDescription',
        defaultMessage: 'Your files will be uploaded in {blobContainerName}'
    }
});

const containerStyles = classNamesFunction<IStackProps, IStackItemStyles>()({
    root: { flex: 1, paddingLeft: '32px', paddingRight: '32px', overflow: 'auto', marginBottom: '10px' }
});

const fromByteToMB = (size: number) => size / 1000 / 1000;
const isFileMaxSizeExceeded = (file: File | AzureBlobStore) => fromByteToMB(file.size) >= LuisConstants.TranslationTool.MaxFileSizeMB;

export const TranslationFilesSelectionPage = observer((props: TranslationFilesSelectionPageProps) => {
    const {
        blobFiles,
        activeStep,
        uploadedDocs,
        storageAccounts,
        isReturningUser,
        storageContainers,
        isBlobFilesLoading,
        translationConfigsDto,
        isStorageAccountsLoading,
        isStorageContainersLoading,
        selectedStorageAccountStore,
        createdStorageContainerName,
        onNext,
        onSubmit,
        onDismiss,
        onPrevious,
        onUploadedDocsChange,
        onChangeStorageContainer,
        onChangeSelectedBlobFiles,
        onChangeSelectedLocalFiles,
        fetchStorageAccountContainers
    } = props;

    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();

    const { uploadLocalFiles } = useTranslatorCapabilities();

    const [selectedFilesLocation, setSelectedFilesLocation] = React.useState<TranslationFileSourceOptions>(
        translationConfigsDto.filesLocation
    );
    const [isFooterLoading, setIsFooterLoading] = React.useState<boolean>(false);
    const [isNextDisabled, setIsNextDisabled] = React.useState<boolean>(false);
    const [isAllFilesSelected, setIsAllFilesSelected] = React.useState<boolean>(
        translationConfigsDto.selectedBlobFiles.length === blobFiles.length && !!blobFiles.length
    );
    const [isChangeLocationModalHidden, setIsChangeLocationModalHidden] = React.useState<boolean>(true);
    const [selectedFolder, setSelectedFolder] = React.useState(null);
    const [chosenContainerName, setChosenContainerName] = React.useState<string>(createdStorageContainerName);

    React.useEffect(() => {
        if (selectedStorageAccountStore) {
            fetchStorageAccountContainers();
        }
    }, [selectedStorageAccountStore]);

    React.useEffect(() => {
        if (translationConfigsDto.sourceContainerName && blobFiles.length === 0) {
            onChangeStorageContainer(translationConfigsDto.sourceContainerName);
        }
    }, []);

    const downloadFile = (fileName: string) => {
        dispatcher.dispatch('downloadFile', {
            fileName,
            storageAccountName: selectedStorageAccountStore?.name,
            containerName: translationConfigsDto.sourceContainerName,
            toastData: {
                failed: { title: localize(messages.downloadedFileFailed), content: localize(messages.downloadedFileFailedDescription) },
                completed: { title: localize(messages.downloadedFileSuccess) }
            }
        });
    };

    const fileLocationOptions: IChoiceGroupOption[] = [
        {
            key: TranslationFileSourceOptions.LocalStorage,
            text: localize(messages.localFilesOption)
        },
        {
            key: TranslationFileSourceOptions.BlobStorage,
            text: localize(messages.blobStoreOption),
            styles: { field: { marginLeft: '20px' } }
        }
    ];

    const getUploadingFolderName = () => {
        const timeStamp = new Date().toISOString();
        const userAlias = rootStore.userStore.email.split('@')[0];

        return `${selectedFolder ?? 'uploaded'}/${userAlias}/${timeStamp}`;
    };

    const onNextClicked = async () => {
        if (selectedFilesLocation === TranslationFileSourceOptions.LocalStorage && translationConfigsDto.selectedLocalFiles.length) {
            setIsFooterLoading(true);
            const filesToUpload = translationConfigsDto.selectedLocalFiles.filter(f => !uploadedDocs.includes(f.name));

            if (filesToUpload.length) {
                const folderName = getUploadingFolderName();
                await uploadLocalFiles({
                    folderName,
                    files: filesToUpload,
                    containerName: chosenContainerName,
                    storageAccountName: selectedStorageAccountStore.name,
                    resourceGroupName: selectedStorageAccountStore?.resourceGroupName,
                    onComplete: containerName => {
                        setIsFooterLoading(false);
                        onNext(
                            selectedFilesLocation,
                            chosenContainerName,
                            translationConfigsDto.selectedLocalFiles.map(file => ({
                                name: file.name,
                                containerName: `${containerName}/${folderName}`
                            })),
                            false,
                            translationConfigsDto.selectedLocalFiles,
                            translationConfigsDto.selectedBlobFiles
                        );
                        onUploadedDocsChange(filesToUpload.map(f => f.name));
                    }
                });
            } else {
                setIsFooterLoading(false);
                onNext(
                    selectedFilesLocation,
                    chosenContainerName,
                    translationConfigsDto.selectedLocalFiles.map(file => ({
                        name: file.name,
                        containerName: chosenContainerName
                    })),
                    false,
                    translationConfigsDto.selectedLocalFiles,
                    translationConfigsDto.selectedBlobFiles
                );
            }
        } else {
            onNext(
                selectedFilesLocation,
                chosenContainerName,
                translationConfigsDto.selectedBlobFiles.map(file => ({
                    name: file.id,
                    containerName: translationConfigsDto.sourceContainerName
                })),
                !!blobFiles.length && translationConfigsDto.selectedBlobFiles.length === blobFiles.length,
                translationConfigsDto.selectedLocalFiles,
                translationConfigsDto.selectedBlobFiles
            );
        }
    };

    const onChangeFileLocationOption = (option: IChoiceGroupOption) => {
        setSelectedFilesLocation(option.key as TranslationFileSourceOptions);
        logDTButtonClick(
            'document translation',
            'jobCreation',
            'radioButton',
            option.key === 'blobStorage' ? 'blobStorage' : 'uploadFiles',
            'filesSelection'
        );
    };

    return (
        <Stack verticalAlign="space-between" verticalFill styles={{ root: { overflow: 'auto' } }}>
            <Stack styles={containerStyles} tokens={{ childrenGap: 21 }}>
                <Stack tokens={{ childrenGap: 13 }}>
                    <LuisText size="20px" fontWeight={600}>
                        {localize(messages.pageTitle)}
                    </LuisText>
                    <LuisText variant="medium">{localize(messages.pageSubtitle)}</LuisText>
                </Stack>

                <Stack tokens={{ childrenGap: 9 }}>
                    <Stack horizontal tokens={{ childrenGap: 7 }} verticalAlign="center">
                        <LuisText variant="medium" fontWeight={600}>
                            {localize(messages.fileOptionsHeader)}
                        </LuisText>
                        {!selectedStorageAccountStore && <Spinner size={SpinnerSize.small} />}
                    </Stack>
                    <Stack.Item>
                        <Stack horizontal tokens={{ childrenGap: 7 }} verticalAlign="center">
                            <ChoiceGroup
                                options={fileLocationOptions}
                                selectedKey={selectedFilesLocation}
                                onChange={(_, opt) => onChangeFileLocationOption(opt)}
                                styles={{ flexContainer: { display: 'flex' } }}
                                disabled={!selectedStorageAccountStore}
                            />
                        </Stack>
                    </Stack.Item>
                    <Stack.Item>
                        <LuisText variant="medium">
                            {localize(messages.fileLocationSubtitle)}
                            &nbsp;
                            {translationConfigsDto.sourceLanguage.key !== LuisConstants.TranslationTool.AutoLanguageDetectionItemKey && (
                                <LuisText>
                                    {localize(messages.fileDisclaimerSubtitle, {
                                        sourceLanguage: translationConfigsDto.sourceLanguage.value
                                    })}
                                </LuisText>
                            )}
                        </LuisText>
                    </Stack.Item>
                </Stack>
                {selectedFilesLocation === TranslationFileSourceOptions.BlobStorage ? (
                    <TranslationFileLocationDropDowns
                        showStorageAccountDropDown
                        isSelectContainerDisabled={!selectedStorageAccountStore}
                        isStorageContainersLoading={isStorageContainersLoading}
                        isStorageAccountsLoading={isStorageAccountsLoading}
                        storageAccountName={translationConfigsDto.storageAccount}
                        selectedDestinationContainer={translationConfigsDto.sourceContainerName}
                        storageAccounts={storageAccounts}
                        storageContainers={storageContainers}
                        onChangeStorageContainer={option => onChangeStorageContainer(option)}
                    />
                ) : (
                    FeatureToggles.UploadDTFileLocation && (
                        <Stack horizontal tokens={{ childrenGap: 5 }}>
                            <LuisText>
                                {localize(messages.selectFileLocationDescription, {
                                    blobContainerName: chosenContainerName
                                })}
                            </LuisText>

                            <Link
                                styles={{ root: { fontSize: '14px' } }}
                                onClick={() => {
                                    setIsChangeLocationModalHidden(false);
                                    logDTButtonClick(
                                        'document translation',
                                        'jobCreation',
                                        'uploadFiles:Button',
                                        'changeFileLocation',
                                        'filesSelection'
                                    );
                                }}>
                                {localize(messages.changeButton)}
                            </Link>
                        </Stack>
                    )
                )}

                <Stack>
                    <TranslationQuotaConsumptionCard
                        filesSizeQuota={LuisConstants.TranslationTool.MaxFilesSizeMB}
                        filesNumberQuota={LuisConstants.TranslationTool.MaxFilesNumber}
                        selectedFilesLocation={selectedFilesLocation}
                        selectedFilesNumber={
                            selectedFilesLocation === TranslationFileSourceOptions.LocalStorage
                                ? translationConfigsDto.selectedLocalFiles.length
                                : translationConfigsDto.selectedBlobFiles.length
                        }
                        selectedFilesSize={
                            selectedFilesLocation === TranslationFileSourceOptions.LocalStorage
                                ? fromByteToMB(translationConfigsDto.selectedLocalFiles.reduce((a, b) => a + (b.size || 0), 0))
                                : fromByteToMB(translationConfigsDto.selectedBlobFiles.reduce((a, b) => a + (b.size || 0), 0))
                        }
                        isFileMaxSizeExceeded={
                            selectedFilesLocation === TranslationFileSourceOptions.LocalStorage
                                ? translationConfigsDto.selectedLocalFiles.some(isFileMaxSizeExceeded)
                                : translationConfigsDto.selectedBlobFiles.some(isFileMaxSizeExceeded)
                        }
                        disableNext={setIsNextDisabled}
                    />
                </Stack>

                <Stack grow>
                    {selectedFilesLocation === TranslationFileSourceOptions.LocalStorage ? (
                        <TranslationLocalFileTable
                            selectedLocalFiles={translationConfigsDto.selectedLocalFiles}
                            onSelectedLocalFilesChange={onChangeSelectedLocalFiles}
                            isLoading={!selectedStorageAccountStore}
                        />
                    ) : (
                        <TranslationBlobStoreTable
                            files={blobFiles}
                            selectedContainer={translationConfigsDto.sourceContainerName}
                            downloadFile={downloadFile}
                            isFilesLoading={isBlobFilesLoading}
                            isAllFilesSelected={isAllFilesSelected}
                            selectedBlobFiles={translationConfigsDto.selectedBlobFiles}
                            onChangeIsAllFilesSelected={setIsAllFilesSelected}
                            onChangeSelectedBlobFiles={onChangeSelectedBlobFiles}
                        />
                    )}
                </Stack>
            </Stack>
            <TranslationUploadFileLocationModal
                hidden={isChangeLocationModalHidden}
                onDismiss={() => setIsChangeLocationModalHidden(!isChangeLocationModalHidden)}
                storageContainers={storageContainers}
                selectedDestinationContainer={chosenContainerName}
                setSelectedDestinationContainer={setChosenContainerName}
                isStorageContainersLoading={isStorageContainersLoading}
                selectedStorageAccount={translationConfigsDto.storageAccount}
                selectedFolder={selectedFolder}
                setSelectedFolder={setSelectedFolder}
            />
            <TranslationToolFooter
                activeStep={activeStep}
                isNextDisabled={
                    (selectedFilesLocation === TranslationFileSourceOptions.BlobStorage &&
                        !translationConfigsDto.selectedBlobFiles.length) ||
                    (selectedFilesLocation === TranslationFileSourceOptions.LocalStorage &&
                        !translationConfigsDto.selectedLocalFiles.length) ||
                    isNextDisabled
                }
                isLoading={isFooterLoading || !selectedStorageAccountStore}
                isReturningUser={isReturningUser}
                onDismiss={onDismiss}
                onNext={onNextClicked}
                onPrevious={onPrevious}
                onSubmit={onSubmit}
            />
        </Stack>
    );
});
